import { Component, EventEmitter, Input, OnInit, Output, inject } from '@angular/core';
import { EMPTY, catchError, finalize, first, forkJoin, tap } from 'rxjs';
import {
  INVENTORY_ITEM_TYPES,
  InventoryItemType,
  getInventoryItemTypeLabelKey,
} from 'src/app/core/enums/inventory-item-type.enum';
import { GoodsCategoryOption } from 'src/app/core/models/goods-category/goods-category-option.interface';
import { GoodsCategory } from 'src/app/core/models/goods-category/goods-category.interface';
import { InventoryItemCreate } from 'src/app/core/models/inventory/inventory-item-create.interface';
import { InventoryItemCreateFormModel } from 'src/app/core/models/inventory/inventory-item-create-form.model';
import { InventoryItem } from 'src/app/core/models/inventory/inventory-item.interface';
import { UnitOfMeasureLookup } from 'src/app/core/models/inventory/unit-of-measure-lookup.interface';
import { GoodsCategoriesService } from 'src/app/core/services/goods-categories.service';
import { InventoryItemService } from 'src/app/core/services/inventory-item.service';

interface ItemTypeOption {
  value: InventoryItemType;
  labelKey: string;
}

@Component({
  selector: 'app-create-goods',
  templateUrl: './create-goods.component.html',
  styleUrls: ['./create-goods.component.scss'],
  providers: [InventoryItemCreateFormModel],
  standalone: false,
})
export class CreateGoodsComponent implements OnInit {
  formModel = inject(InventoryItemCreateFormModel);
  private readonly inventoryItemService = inject(InventoryItemService);
  private readonly goodsCategoriesService = inject(GoodsCategoriesService);

  @Input({ required: true }) public companyId = '';

  @Output() public cancelled = new EventEmitter<void>();
  @Output() public created = new EventEmitter<InventoryItem>();

  public categoryOptions: GoodsCategoryOption[] = [];
  public unitOfMeasureOptions: UnitOfMeasureLookup[] = [];
  public readonly itemTypeOptions: readonly ItemTypeOption[] = INVENTORY_ITEM_TYPES.map((value) => ({
    value,
    labelKey: getInventoryItemTypeLabelKey(value),
  }));
  public isLoadingCategories = false;
  public isSubmitting = false;

  ngOnInit(): void {
    this.loadLookups();
  }

  public onCancel(): void {
    this.cancelled.emit();
  }

  public submitForm(): void {
    if (this.formModel.form.invalid) {
      this.formModel.form.markAllAsTouched();
      return;
    }

    const payload: InventoryItemCreate = {
      ...this.formModel.form.getRawValue(),
      baseUnitOfMeasureId: this.formModel.form.controls.baseUnitOfMeasureId.value!,
    };

    this.isSubmitting = true;

    this.inventoryItemService
      .createInventoryItem(this.companyId, payload)
      .pipe(
        first(),
        tap((inventoryItem) => this.created.emit(inventoryItem)),
        catchError(() => EMPTY),
        finalize(() => {
          this.isSubmitting = false;
        }),
      )
      .subscribe();
  }

  private loadLookups(): void {
    this.isLoadingCategories = true;

    forkJoin({
      goodsCategories: this.goodsCategoriesService.getCompanyGoodsCategories(this.companyId),
      unitsOfMeasure: this.inventoryItemService.getUnitsOfMeasureLookup(this.companyId),
    })
      .pipe(
        first(),
        tap(({ goodsCategories, unitsOfMeasure }) => {
          this.categoryOptions = this.flattenGoodsCategories(goodsCategories);
          this.unitOfMeasureOptions = unitsOfMeasure;
        }),
        catchError(() => {
          this.categoryOptions = [];
          this.unitOfMeasureOptions = [];
          return EMPTY;
        }),
        finalize(() => {
          this.isLoadingCategories = false;
        }),
      )
      .subscribe();
  }

  private flattenGoodsCategories(
    goodsCategories: GoodsCategory[],
    depth = 0,
  ): GoodsCategoryOption[] {
    return goodsCategories.flatMap((category) => {
      const currentItem: GoodsCategoryOption = {
        id: category.id,
        title: category.title,
        depth,
        label: `${depth ? '— '.repeat(depth) : ''}${category.title}`,
      };

      return [currentItem, ...this.flattenGoodsCategories(category.children, depth + 1)];
    });
  }
}
